import type {BomTreeNode} from '@/matic/features/cetec-integration/types';
import type {ObjectRecord} from '@/matic/features/master-data/types/master-data-types';

/**
 * Canonical field keys from the ERP Part sync spec
 * (`cetec_entity_sync_specs.py`'s `PART_SPEC`).
 *
 * These are keys *we* own: the tenant may rename the ObjectType and add any
 * fields they like, but the sync writes these exact keys whenever the target
 * type was auto-created. A tenant on an admin-picked target type has a
 * `field_map` renaming them — that map is deliberately not exposed on the
 * read-side provenance endpoint, so this surface degrades rather than guesses
 * (see `resolveErpColumns`).
 */
export const PART_FIELD_KEYS = {
  name: 'name',
  partNumber: 'part_number',
  description: 'description',
  qtyOnHand: 'qty_on_hand',
  qtyRequired: 'qty_required',
  qtyAvailable: 'qty_available',
  isShort: 'is_short',
  isBom: 'is_bom',
} as const;

/** The ERP entity kind this surface is bound to. Mirrors `ErpEntityKind.PART`. */
export const PART_ENTITY_KIND = 'part';

/**
 * Canonical field keys from the ERP PO Line sync spec
 * (`cetec_entity_sync_specs.py`'s `PO_LINE_SPEC`). Same "keys we own"
 * caveat as `PART_FIELD_KEYS`.
 */
export const PO_LINE_FIELD_KEYS = {
  name: 'name',
  poNumber: 'po_number',
  lineNumber: 'line_number',
  partNumber: 'part_number',
  orderQty: 'order_qty',
  qtyReceived: 'qty_received',
  qtyRemaining: 'qty_remaining',
  status: 'status',
  vendorName: 'vendor_name',
  cost: 'cost',
} as const;

/** The ERP entity kind for open PO lines. Mirrors `ErpEntityKind.PO_LINE`. */
export const PO_LINE_ENTITY_KIND = 'po_line';

/**
 * One row in the tracker.
 *
 * `id` is the row's stable identity and differs by mode on purpose. In flat
 * mode it is the MDM record id. In tree mode it is the **BOM path**, because
 * one part legitimately occupies several positions in one tree — Chester's own
 * sheet has `10510002003` three times — and keying by part number would make
 * those positions share expand state and collide in the row model.
 */
export type ProcurementRow = {
  id: string;
  partNumber: string;
  /**
   * The MDM record behind this row. `undefined` when the BOM names a part
   * master data doesn't carry — rendered as an explicit gap rather than
   * guessed at, since BOM component keys can carry a revision suffix
   * (`16181113E00 REV-A`) that never matches a bare part number.
   */
  record: ObjectRecord | undefined;
  /** BOM position facts. `undefined` in flat mode, which has no positions. */
  node: BomTreeNode | undefined;
  depth: number;
  /** How many positions this part occupies across the loaded trees. */
  positionCount: number;
  children: ProcurementRow[];
};
